// @ts-strict-ignore
import isEqual from 'lodash/isEqual';

import {formatDate, parseDate, relativeToAbsoluteDate} from 'common/query-builder/cohorts-query-builder/util/index';
import {DateRange} from 'common/widgets/date-range-picker/models/date-range';
import {BaseDate, RelativeDate} from 'common/types/reports/bookmark';

import {PropertyFilter} from '../builder/property-filter';
import {ANY_EVENT} from '../constants';
import {DateRangeType} from '../enums/builder-enums';
import {BehaviorCountType, LogicalOperator} from '../enums/expression-enums';
import {StagedBehaviorDateRange} from '../types/builder-state-types';
import {
  ReportBasedCohorts,
  Serializable,
  SerializedBehavior,
  SerializedBehaviorDictionary,
  SerializedFunnelStep,
} from '../types/serialized-cohort-types';
import {BehaviorCount} from './behavior-count';
import {BehaviorWindow} from './behavior-window';
import {EventSelector} from './event-selector';
import {Funnel} from './funnel';
import {FunnelStep} from './funnel-step';
import {Report} from './report';

export interface BehaviorsDictionary {
  [behavior: string]: Behavior;
}

export class Behavior implements Serializable {
  public static parseBehavior(serializedBehavior: SerializedBehavior) {
    const count = BehaviorCount.parseBehaviorCount(serializedBehavior.count);
    const serializedFunnel = serializedBehavior.funnel;
    let funnel = null;
    if (serializedFunnel) {
      funnel = new Funnel(
        serializedFunnel.steps.map((step: SerializedFunnelStep) => {
          return new FunnelStep(BehaviorCount.parseBehaviorCount(step.count), step.value);
        }),
      );
    }

    let report = null;

    const serializedFunnelReport = serializedBehavior[ReportBasedCohorts.Funnels];
    if (serializedFunnelReport) {
      report = new Report({
        name: ReportBasedCohorts.Funnels,
        bookmark: serializedFunnelReport.bookmark,
        behavior_params: serializedFunnelReport.behavior_params,
      });
    }

    const serializedFlowsReport = serializedBehavior[ReportBasedCohorts.Flows];
    if (serializedFlowsReport) {
      report = new Report({
        name: ReportBasedCohorts.Flows,
        bookmark: serializedFlowsReport.bookmark,
        behavior_params: serializedFlowsReport.behavior_params,
      });
    }

    const serializedRetentionReport = serializedBehavior[ReportBasedCohorts.Retention];
    if (serializedRetentionReport) {
      report = new Report({
        name: ReportBasedCohorts.Retention,
        bookmark: serializedRetentionReport.bookmark,
        behavior_params: serializedRetentionReport.behavior_params,
        average_behavior_params: serializedRetentionReport.average_behavior_params,
      });
    }

    const serializedAddictionReport = serializedBehavior[ReportBasedCohorts.Addiction];
    if (serializedAddictionReport) {
      report = new Report({
        name: ReportBasedCohorts.Addiction,
        bookmark: serializedAddictionReport.bookmark,
        behavior_params: serializedAddictionReport.behavior_params,
        average_behavior_params: serializedAddictionReport.average_behavior_params,
      });
    }

    const serializedWindow = serializedBehavior.window;
    let behaviorWindow = null;
    if (serializedWindow) {
      behaviorWindow = new BehaviorWindow(serializedWindow.unit, serializedWindow.value);
    }

    return new Behavior(
      count,
      funnel,
      report,
      serializedBehavior.from_date,
      serializedBehavior.to_date,
      behaviorWindow,
    );
  }

  public static getReportName(serializedBehaviors: SerializedBehaviorDictionary) {
    const isAddictionReport = Object.values(serializedBehaviors).some((serializedBehavior: SerializedBehavior) => {
      return Boolean(serializedBehavior[ReportBasedCohorts.Addiction]);
    });

    const isFlowsReport = Object.values(serializedBehaviors).some((serializedBehavior: SerializedBehavior) => {
      return Boolean(serializedBehavior[ReportBasedCohorts.Flows]);
    });

    const isFunnelsReport = Object.values(serializedBehaviors).some((serializedBehavior: SerializedBehavior) => {
      return Boolean(serializedBehavior[ReportBasedCohorts.Funnels]);
    });

    const isRetentionReport = Object.values(serializedBehaviors).some((serializedBehavior: SerializedBehavior) => {
      return Boolean(serializedBehavior[ReportBasedCohorts.Retention]);
    });

    if (isAddictionReport) {
      return `addiction`;
    } else if (isFlowsReport) {
      return `flows`;
    } else if (isFunnelsReport) {
      return `funnels`;
    } else if (isRetentionReport) {
      return `retention`;
    }
    return null;
  }

  public static parseBehaviors(serializedBehaviors: SerializedBehaviorDictionary): BehaviorsDictionary {
    if (!serializedBehaviors) {
      return null;
    }
    return Object.keys(serializedBehaviors).reduce((behaviorsDictionary: BehaviorsDictionary, behaviorName: string) => {
      const serializedBehavior = serializedBehaviors[behaviorName];
      behaviorsDictionary[behaviorName] = Behavior.parseBehavior(serializedBehavior);
      return behaviorsDictionary;
    }, {});
  }

  /**
   * A key used to refer to the behavior's entry in the cohort behavior dictionary
   */
  public declare key: string;

  constructor(
    public count?: BehaviorCount,
    public funnel?: Funnel,
    public report?: Report,
    public fromDate?: BaseDate,
    public toDate?: BaseDate,
    public behaviorWindow?: BehaviorWindow,
  ) {}

  public getEvent(): string {
    return this.count.eventSelector.event;
  }

  public setEvent(event: string): Behavior {
    const count = new BehaviorCount(new EventSelector(event, this.count.eventSelector.selector), this.count.type);
    return new Behavior(count, this.funnel, this.report, this.fromDate, this.toDate, this.behaviorWindow);
  }

  public getDateRange(): {fromDate?: BaseDate; toDate?: BaseDate} {
    if (this.behaviorWindow) {
      return {
        fromDate: formatDate(relativeToAbsoluteDate(this.behaviorWindow.value, this.behaviorWindow.unit), {iso: true}),
        toDate: formatDate(new Date(), {iso: true}),
      };
    }
    return {
      fromDate: this.fromDate,
      toDate: this.toDate,
    };
  }

  public getDateRangeLabel(): string {
    if (this.behaviorWindow) {
      return this.behaviorWindow.toDateRangeString();
    }
    const rolling = DateRange.isRelativeDate(this.fromDate);
    if (isEqual(this.fromDate, this.toDate) || !this.toDate) {
      if (rolling) {
        const fromDateStr = DateRange.convertRelativeDateToCalendarDate(this.fromDate as RelativeDate);
        return `${formatDate(parseDate(fromDateStr))} (rolling)`;
      }
      return formatDate(parseDate(this.fromDate as string));
    }
    if (rolling) {
      const fromDateStr = DateRange.convertRelativeDateToCalendarDate(this.fromDate as RelativeDate);
      const toDateStr = DateRange.convertRelativeDateToCalendarDate(this.toDate as RelativeDate);
      return `${formatDate(parseDate(fromDateStr))} - ${formatDate(parseDate(toDateStr))} (rolling range)`;
    }
    return `${formatDate(parseDate(this.fromDate as string))} - ${formatDate(parseDate(this.toDate as string))}`;
  }

  public producesEmptySelector(): boolean {
    if (this.count) {
      return this.count.eventSelector.event === ANY_EVENT;
    } else if (this.funnel) {
      return this.funnel.steps.some((step) => step.count.eventSelector.event === ANY_EVENT);
    }
    return false;
  }

  public getDateRangeOptions(): StagedBehaviorDateRange {
    let options = {} as StagedBehaviorDateRange;
    if (this.behaviorWindow) {
      options = {
        customRangeType: DateRangeType.Relative,
        unit: this.behaviorWindow.unit,
        value: this.behaviorWindow.value,
      };
    } else {
      const {fromDate, toDate} = this.getDateRange();
      options.fromDate = fromDate;
      options.toDate = toDate;
      if (!toDate) {
        options.customRangeType = DateRangeType.Since;
      } else if (isEqual(toDate, fromDate)) {
        options.customRangeType = DateRangeType.On;
      } else if (toDate > fromDate || (DateRange.isRelativeDate(toDate) && DateRange.isRelativeDate(fromDate))) {
        options.customRangeType = DateRangeType.Between;
      }
    }
    return options;
  }

  // fromDate - 2017-07-01
  // toDate   - 2017-07-15
  public setDateRange(fromDate: BaseDate, toDate: BaseDate): Behavior {
    return new Behavior(this.count, this.funnel, this.report, fromDate, toDate, null);
  }

  public setCountType(countType: BehaviorCountType): Behavior {
    const behaviorCount = new BehaviorCount(this.count.eventSelector, countType);
    return new Behavior(behaviorCount, this.funnel, this.report, this.fromDate, this.toDate, this.behaviorWindow);
  }

  public setWindow(behaviorWindow: BehaviorWindow): Behavior {
    return new Behavior(this.count, this.funnel, this.report, null, null, behaviorWindow);
  }

  public setEventSelector(propertyFilters: PropertyFilter[], operator: LogicalOperator): Behavior {
    if (this.report) {
      return new Behavior(this.count, null, this.report, this.fromDate, this.toDate, this.behaviorWindow);
    }
    return new Behavior(
      this.count.setEventSelector(propertyFilters, operator),
      null,
      null,
      this.fromDate,
      this.toDate,
      this.behaviorWindow,
    );
  }

  public isReportBasedCohort() {
    return Object.values(ReportBasedCohorts).includes(this.report?.name);
  }

  public serialize(): SerializedBehavior {
    const serializedBehavior: SerializedBehavior = {};
    if (this.count) {
      serializedBehavior.count = this.count.serialize();
    } else if (this.funnel) {
      serializedBehavior.funnel = this.funnel.serialize();
    } else if (this.report) {
      Object.assign(serializedBehavior, this.report.serialize());
      // early return b/c don't need window or from_date or to_date
      return serializedBehavior;
    }

    if (this.behaviorWindow) {
      serializedBehavior.window = this.behaviorWindow.serialize();
    } else {
      serializedBehavior.from_date = this.fromDate;
      if (this.toDate) {
        serializedBehavior.to_date = this.toDate;
      }
    }

    return serializedBehavior;
  }
}
